import { restApi } from '../../api/restApiSlice';
import { legacyAPIRequest } from '../../api/legacyAPIRequest';

import VerificationResult from '../VerificationResult';

export const integrationWizardApiSlice = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getDomain: builder.query({
      query: () => '/v3/integration-wizard/domain',
      // `setDomain` invalidates this tag, but nothing provided it — so the
      // cached domain survived a change of domain mid-wizard. The render proof
      // reads this value to decide which homepage to prove, so a stale hit
      // meant proving the previous site.
      providesTags: ['IntegrationDomain'],
    }),
    setDomain: builder.mutation({
      query: (domain) => ({
        url: '/v3/integration-wizard/domain',
        method: 'POST',
        body: { domain },
      }),
      invalidatesTags: ['IntegrationDomain'],
    }),
    getDomainStats: builder.query({
      query: (domain) => `/v3/integration-wizard/domain/${domain}/stats`,
    }),
    getDomainSitemaps: builder.query({
      query: (domain) => `/v3/integration-wizard/domain/${domain}/sitemaps`,
    }),
    verifyDomain: builder.mutation<
      {
        validationResult: VerificationResult;
        url: string;
      },
      { url: string; integrationWizardId?: string }
    >({
      queryFn: async (body) => {
        try {
          const rawResult = await legacyAPIRequest({
            url: 'v1/check-integration',
            method: 'POST',
            body: {
              url: body.url,
              ...(body.integrationWizardId ? { integrationWizardId: body.integrationWizardId } : {}),
            },
            signal: AbortSignal.timeout(2 * 60 * 1000),
          });
          if (!rawResult.ok) throw new Error('Failed to verify domain');
          const result = await rawResult.json();
          return {
            data: {
              validationResult: result,
              url: body.url,
            },
          };
        } catch (err) {
          const error = err as Error;
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      invalidatesTags: ['domainStatistics', 'Domains'],
    }),
  }),
});

export const {
  useGetDomainQuery,
  useSetDomainMutation,
  useGetDomainStatsQuery,
  useGetDomainSitemapsQuery,
  useVerifyDomainMutation,
} = integrationWizardApiSlice;
